import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  CircleHelp,
  MessageSquarePlus,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  Sun,
  Zap,
} from 'lucide-react';
import { Button, Icon, IconButton, cx } from '../../components/ui';
import { useFunMode, useT } from '../i18n';
import { LogoIcon } from './LogoIcon';

/**
 * Per-item accent hue. Each maps to a foundation accent pair
 * (`--c-accent-<hue>` / `--c-accent-<hue>-text`). Only applied in Fun Mode — in
 * plain mode every selected row uses the one restrained brand tint. See
 * `accentActive` / `activeClasses` below.
 */
export type AccentHue =
  | 'indigo'
  | 'violet'
  | 'sky'
  | 'emerald'
  | 'cyan'
  | 'amber'
  | 'rose'
  | 'teal'
  | 'orange'
  | 'pink'
  | 'fuchsia';

export interface RailNavItem {
  key: string;
  to: string;
  label: string;
  icon: ReactNode;
  active: boolean;
  /** Fun-mode selected hue. Ignored in plain mode (uniform brand tint). */
  accent?: AccentHue;
  /** Unread/pending count — renders a red count badge when > 0. */
  badge?: number;
}

export interface SidebarRailViewProps {
  expanded: boolean;
  onToggleExpand: () => void;
  /** Hide the collapse/expand control (mobile renders inside an overlay menu). */
  hideExpandToggle?: boolean;
  navItems: RailNavItem[];
  isDark: boolean;
  onToggleTheme: () => void;
  onSearch: () => void;
  /** Opens the quicklinks manager popup. */
  onQuicklinks: () => void;
  /** Opens the global feedback dialog. */
  onFeedback: () => void;
  notificationsActive?: boolean;
  /** Unread notifications — renders a red count badge on the bell when > 0. */
  notificationsCount?: number;
  settingsActive?: boolean;
  helpActive?: boolean;
  profile: { name: string; email?: string; picture?: string; to: string };
}

const rowBase =
  'group flex items-center rounded-xl no-underline text-fg-secondary outline-none transition ' +
  'hover:bg-neutral hover:text-fg focus-visible:ring-2 focus-visible:ring-[rgb(var(--c-ring)/0.5)]';
const rowSize = (expanded: boolean) =>
  expanded
    ? 'h-11 w-full justify-start gap-3 px-3.5'
    : 'size-10 justify-center';

/**
 * Selected-state classes — one restrained brand tint for every item (a soft
 * 12%-opacity brand fill + brand-tinted icon/label). Uniform rather than a
 * per-item hue so the rail reads calm and professional; hover is pinned to the
 * same tint so an active row doesn't fall back to the neutral hover. The brand
 * token re-points per palette (see the `[data-palette]` blocks in tailwind.css).
 */
const rowActive =
  'bg-brand-solid/12 text-brand-text hover:bg-brand-solid/12 hover:text-brand-text';

/**
 * Fun-mode selected-state classes per accent hue: a 12%-opacity tinted
 * background plus the matching label color (the icon inherits it via
 * `currentColor`). Full literal class strings — the standalone Tailwind CLI
 * scans source for class names, so these can't be built by interpolation. The
 * hue → token pairs resolve to the foundation `--c-accent-*` vars, which
 * re-point per palette (see the `[data-palette]` blocks in tailwind.css).
 */
const accentActive: Record<AccentHue, string> = {
  indigo:
    'bg-accent-indigo/12 text-accent-indigo-text hover:bg-accent-indigo/12 hover:text-accent-indigo-text',
  violet:
    'bg-accent-violet/12 text-accent-violet-text hover:bg-accent-violet/12 hover:text-accent-violet-text',
  sky: 'bg-accent-sky/12 text-accent-sky-text hover:bg-accent-sky/12 hover:text-accent-sky-text',
  emerald:
    'bg-accent-emerald/12 text-accent-emerald-text hover:bg-accent-emerald/12 hover:text-accent-emerald-text',
  cyan: 'bg-accent-cyan/12 text-accent-cyan-text hover:bg-accent-cyan/12 hover:text-accent-cyan-text',
  amber:
    'bg-accent-amber/12 text-accent-amber-text hover:bg-accent-amber/12 hover:text-accent-amber-text',
  rose: 'bg-accent-rose/12 text-accent-rose-text hover:bg-accent-rose/12 hover:text-accent-rose-text',
  teal: 'bg-accent-teal/12 text-accent-teal-text hover:bg-accent-teal/12 hover:text-accent-teal-text',
  orange:
    'bg-accent-orange/12 text-accent-orange-text hover:bg-accent-orange/12 hover:text-accent-orange-text',
  pink: 'bg-accent-pink/12 text-accent-pink-text hover:bg-accent-pink/12 hover:text-accent-pink-text',
  fuchsia:
    'bg-accent-fuchsia/12 text-accent-fuchsia-text hover:bg-accent-fuchsia/12 hover:text-accent-fuchsia-text',
};

/** Active row classes — the per-item hue in fun mode, else the brand tint. */
const activeClasses = (funMode: boolean, accent?: AccentHue) =>
  funMode && accent ? accentActive[accent] : rowActive;

/** Cap the displayed count so the badge stays a tidy pill. */
const formatCount = (count: number) => (count > 99 ? '99+' : String(count));

/**
 * Red unread-count badge. Expanded: a trailing pill pushed to the row's end.
 * Collapsed: a compact pill overlapping the icon's top-right, ringed in the
 * surface color so it reads as a cutout against the icon.
 */
const CountBadge = ({
  count,
  expanded,
}: {
  count: number;
  expanded: boolean;
}) =>
  expanded ? (
    <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-error-500 px-1.5 text-xs font-semibold tabular-nums text-base-white shadow-xs ring-1 ring-inset ring-white/20">
      {formatCount(count)}
    </span>
  ) : (
    <span
      aria-hidden
      className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-surface bg-error-500 px-1 text-[0.625rem] font-semibold leading-none tabular-nums text-base-white"
    >
      {formatCount(count)}
    </span>
  );

/** Nav row — a routing Link (collapsed icon-only with tooltip, icon+label expanded). */
const RailLink = ({
  item,
  expanded,
}: {
  item: RailNavItem;
  expanded: boolean;
}) => {
  const t = useT();
  const funMode = useFunMode();
  const hasBadge = typeof item.badge === 'number' && item.badge > 0;
  return (
    <Link
      to={item.to}
      aria-label={
        hasBadge
          ? t('nav.sidebarRail.badgeAriaLabel', {
              label: item.label,
              count: item.badge!,
            })
          : item.label
      }
      aria-current={item.active ? 'page' : undefined}
      title={expanded ? undefined : item.label}
      className={cx(
        'relative',
        rowBase,
        rowSize(expanded),
        item.active && activeClasses(funMode, item.accent),
      )}
    >
      <span
        className={cx(
          'flex shrink-0',
          // Fun mode only: bouncy pop+wiggle on hover, gentle idle bob when active.
          funMode && 'fun-hover-wiggle',
          funMode && item.active && 'animate-fun-bob',
        )}
      >
        {item.icon}
      </span>
      {expanded && (
        <span className="truncate text-sm font-medium">{item.label}</span>
      )}
      {hasBadge && <CountBadge count={item.badge!} expanded={expanded} />}
    </Link>
  );
};

/** Action row — uses the Button primitive (icon-only collapsed, icon+label expanded). */
const RailAction = ({
  label,
  icon,
  expanded,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  expanded: boolean;
  onClick: () => void;
}) =>
  expanded ? (
    <Button
      variant="tertiary"
      aria-label={label}
      onPress={onClick}
      iconLeading={icon}
      fullWidth
      className="h-11 justify-start gap-3 rounded-xl bg-transparent px-3.5 text-fg-secondary hover:bg-neutral hover:text-fg"
    >
      <span className="truncate font-medium">{label}</span>
    </Button>
  ) : (
    <IconButton variant="ghost" aria-label={label} onPress={onClick}>
      {icon}
    </IconButton>
  );

/**
 * Presentational sidebar rail — pure props, no Backstage hooks (Storybook-able).
 * Theme-aware (flips with the active theme); nav rows are routing Links, action
 * rows compose the Button / IconButton primitives. No hand-rolled buttons.
 */
export const SidebarRailView = ({
  expanded,
  onToggleExpand,
  hideExpandToggle,
  navItems,
  isDark,
  onToggleTheme,
  onSearch,
  onQuicklinks,
  onFeedback,
  notificationsActive,
  notificationsCount,
  settingsActive,
  helpActive,
  profile,
}: SidebarRailViewProps) => {
  const t = useT();
  const homeAriaLabel = t('nav.sidebarRail.homeAriaLabel');
  // Section 1 groups the personal shortcuts (Search / Quicklinks / Home);
  // section 2 is the rest of the nav. Home is pulled out of navItems by key.
  const homeItem = navItems.find(item => item.key === 'home');
  const mainItems = navItems.filter(item => item.key !== 'home');
  return (
    <div
      className={cx(
        'flex h-full w-full flex-col gap-1',
        expanded ? 'items-stretch px-3 py-3' : 'items-center py-3',
      )}
    >
      {expanded ? (
        <div className="mb-2 flex items-center gap-2 px-1.5">
          <Link
            to="/"
            aria-label={homeAriaLabel}
            className="flex min-w-0 items-center gap-2 no-underline"
          >
            <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-black">
              <LogoIcon className="block h-6 w-auto" />
            </span>
            <span className="font-pixel animate-text-glow text-xl text-fg">
              {t('nav.sidebarRail.wordmark')}
            </span>
          </Link>
          {!hideExpandToggle && (
            <span className="ml-auto">
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={t('nav.sidebarRail.collapseAriaLabel')}
                onPress={onToggleExpand}
              >
                <Icon as={PanelLeftClose} size="sm" />
              </IconButton>
            </span>
          )}
        </div>
      ) : (
        <>
          <Link
            to="/"
            aria-label={homeAriaLabel}
            className="mb-1.5 flex justify-center no-underline"
          >
            <span className="inline-flex size-11 items-center justify-center rounded-lg bg-black">
              <LogoIcon className="block h-6 w-auto" />
            </span>
          </Link>
          {!hideExpandToggle && (
            <RailAction
              label={t('nav.sidebarRail.expandLabel')}
              expanded={false}
              onClick={onToggleExpand}
              icon={<Icon as={PanelLeftOpen} size="md" />}
            />
          )}
        </>
      )}

      <RailAction
        label={t('nav.sidebarRail.searchLabel')}
        expanded={expanded}
        onClick={onSearch}
        icon={<Icon as={Search} size="md" />}
      />

      <RailAction
        label={t('nav.sidebarRail.quicklinksLabel')}
        expanded={expanded}
        onClick={onQuicklinks}
        icon={<Icon as={Zap} size="md" />}
      />

      {homeItem && (
        <RailLink key={homeItem.key} item={homeItem} expanded={expanded} />
      )}

      {/* Spacing (no rule) separates section 1 (Search/Quicklinks/Home) from
          the main nav — see the grouped-with-gaps sidebar pattern. */}
      <div aria-hidden className="h-5" />

      {mainItems.map(item => (
        <RailLink key={item.key} item={item} expanded={expanded} />
      ))}

      <span className="flex-1" />

      <RailLink
        item={{
          key: 'notifications',
          to: '/notifications',
          label: t('nav.sidebarRail.notificationsLabel'),
          active: Boolean(notificationsActive),
          accent: 'orange',
          badge: notificationsCount,
          icon: <Icon as={Bell} size="md" />,
        }}
        expanded={expanded}
      />
      <RailLink
        item={{
          key: 'settings',
          to: '/settings',
          label: t('nav.sidebarRail.settingsLabel'),
          active: Boolean(settingsActive),
          accent: 'pink',
          icon: <Icon as={Settings} size="md" />,
        }}
        expanded={expanded}
      />
      <RailLink
        item={{
          key: 'help',
          to: '/help',
          label: t('nav.sidebarRail.helpLabel'),
          active: Boolean(helpActive),
          accent: 'fuchsia',
          icon: <Icon as={CircleHelp} size="md" />,
        }}
        expanded={expanded}
      />
      <RailAction
        label={t('nav.sidebarRail.feedbackLabel')}
        expanded={expanded}
        onClick={onFeedback}
        icon={<Icon as={MessageSquarePlus} size="md" />}
      />
      <RailAction
        label={
          isDark
            ? t('nav.sidebarRail.themeToLight')
            : t('nav.sidebarRail.themeToDark')
        }
        expanded={expanded}
        onClick={onToggleTheme}
        icon={<Icon as={isDark ? Sun : Moon} size="md" />}
      />

      <Link
        to={profile.to}
        aria-label={t('nav.sidebarRail.profileAriaLabel')}
        title={expanded ? undefined : profile.name}
        className={cx(
          rowBase,
          expanded
            ? 'h-auto w-full justify-start gap-3 px-3 py-2'
            : 'size-10 justify-center',
        )}
      >
        {profile.picture ? (
          <img
            className="size-7 shrink-0 rounded-full object-cover"
            src={profile.picture}
            alt=""
          />
        ) : (
          <span
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-solid/15 text-xs font-semibold text-brand-text"
            aria-hidden
          >
            {(profile.name?.[0] ?? '?').toUpperCase()}
          </span>
        )}
        {expanded && (
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-fg">
              {profile.name}
            </span>
            {profile.email && (
              <span className="truncate text-xs text-fg-tertiary">
                {profile.email}
              </span>
            )}
          </span>
        )}
      </Link>
    </div>
  );
};
